import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import type { ReviewState } from '@chess/api-review';
import { useSelect } from '@chess/api-review/react';
import { useConstraint, useConstraintOwner, useConstraintRegistry } from '@chess/api-constraints/react';
import { uciToInput } from '@chess/api-chess';
import { PUZZLES_API, type Puzzles } from '@chess/api-puzzles';
import { positionAt, type ShortlistReading } from '@chess/api-review';
import { stepControlsView, type StepAction } from './controls.ts';
import { MAIN_LINE_PLIES } from './mistake-actions.ts';
import { isTyping, keyAction } from './keys.ts';
import { StepControls } from './StepControls.tsx';
import { useReview } from './use-review.ts';
import './step-controls.css';

// The steps under the board, after v1's ReviewStepControls: a bar of two
// zones (2026-10-14; three with the steps centred before). At the left end
// the four buttons to move through the game: the inner two step, the outer
// two go to the previous and the next of the reader's mistakes, or to the
// start and the end when no mistake lies that way (outer-steps.ts,
// 2026-10-13; ↑ and ↓ do the same). At the right end the contextual exit;
// on the game's own position before one of the reader's blunders or misses,
// Puzzle (P), which sets the position up to solve through the puzzles API;
// and from the end of the opening on, on the game or on a line of the
// reader's own, Main line (L), which plays what the board's green arrow
// shows, the shortlist's live reading of the position (the scan's best move
// until it is in), again and again for a few moves or to a mate
// (mistake-actions.ts), leaving the reader on a line with the exit at the
// right. Main line stands beside Puzzle or the exit, never hidden by them. The arrow
// keys do the same as the centre: left and right step, up and down jump to
// either end. When the reader has left the game for a line of their own, the
// forward two are disabled while the back two stay: back takes back the
// line's last move, one at a time, so another move can be tried at any point
// (the last take-back lands on the branch ply), and start drops the line.
// The exit is then "Back to the game", which returns to the ply the line
// branched from; Escape does the same. The board's Controls constraint
// switches the directions off, or, with a range (a puzzle), makes them look
// along the path within it; the session enforces it, so the buttons call
// the usual steps and only show what the constraint allows: while an owner
// holds the controls (a puzzle) the centre draws only look back (‹) and back
// to the puzzle (↶), first, next and end left out rather than greyed, so the
// puzzle's controls read as a different mode, and the exit is "Back to the
// puzzle" while looking back. Reads the review interface and the constraint
// registry; owns nothing. What is drawn, enabled and said is decided in
// `controls.ts`, drawn by `StepControls.tsx`; what a key does is in `keys.ts`.

const IDLE: ReviewState = { status: 'idle', ply: 0 };
/** The least pause between the main line's moves as it plays out: the puzzle solution's pace; the shortlist's own time when longer. */
const MAIN_LINE_STEP_MS = 600;
/** How long Main line waits for the shortlist to read a position before giving up on that move. */
const MAIN_LINE_WAIT_MS = 6000;

export function setup(app: PiletApi) {
  // The shortlist's reading of the position on the board, the board's green arrow's source.
  const shortlistRef: { current: ShortlistReading } = { current: {} };
  app.on('review.shortlist', (reading: ShortlistReading) => {
    shortlistRef.current = reading;
  });

  const Panel: React.FC = () => {
    const review = useReview(app);
    const state = useSelect(review, (s) => s) ?? IDLE;
    const registry = useConstraintRegistry(app);
    const controls = useConstraint(registry, state.scope, 'controls');
    const owner = useConstraintOwner(registry, state.scope);
    const view = stepControlsView(state, controls, owner);
    const rangedRef = React.useRef(view.ranged);
    rangedRef.current = view.ranged;
    const outerRef = React.useRef(view.outer);
    outerRef.current = view.outer;
    const rightRef = React.useRef(view.zones.right);
    rightRef.current = view.zones.right;
    const mainLineRef = React.useRef(view.mainLine);
    mainLineRef.current = view.mainLine;
    // Main line: what the board's green arrow shows, played. The arrow is
    // the shortlist's live reading of the position (`review.shortlist`, the
    // same event the board reads), the scan's best move until that reading
    // is in; so each step waits for the shortlist to read the position the
    // board shows, takes the best move it shows then, plays it, and goes on,
    // a fixed stretch or a mate. Playing anything else than the arrow would
    // contradict the board (2026-10-14, after it asked the engine itself at
    // a shallower depth and did). Stops the moment the reader moves the
    // board themselves, or presses again.
    const lineRun = React.useRef(0);
    const arrowBest = (): string | undefined => {
      const state = review!.state();
      const fen = positionAt(state);
      if (!fen) return undefined;
      const live = shortlistRef.current;
      if (live.fen === fen && live.best) return live.best.uci;
      // The scan's reading of a game position, as the board falls back to it.
      if (state.exploring || state.browse !== undefined) return undefined;
      return state.scan?.position(state.ply)?.lines[0]?.uci;
    };
    const playMainLine = async () => {
      if (!review) return;
      const mine = ++lineRun.current;
      for (let i = 0; i < MAIN_LINE_PLIES; i++) {
        const fen = positionAt(review.state());
        if (!fen || mine !== lineRun.current) return;
        const started = Date.now();
        // The arrow for this position, as soon as there is one; a beat more so the shortlist deepens it.
        let best = arrowBest();
        while (!best && Date.now() - started < MAIN_LINE_WAIT_MS) {
          await new Promise((r) => setTimeout(r, 100));
          if (mine !== lineRun.current || positionAt(review.state()) !== fen) return;
          best = arrowBest();
        }
        const rest = MAIN_LINE_STEP_MS - (Date.now() - started);
        if (rest > 0) await new Promise((r) => setTimeout(r, rest));
        if (mine !== lineRun.current || positionAt(review.state()) !== fen) return;
        best = arrowBest() ?? best;
        if (!best || !review.play(uciToInput(best))) return;
      }
    };
    const perform = React.useCallback(
      (action: StepAction) => {
        if (!review) return;
        if (action === 'puzzle') {
          const right = rightRef.current;
          if (right?.kind === 'mistake') void app.getApi<Puzzles>(PUZZLES_API).then((p) => p.startForMove(right.ply));
          return;
        }
        if (action === 'mainLine') {
          if (mainLineRef.current) void playMainLine();
          return;
        }
        if (action === 'outerBack' || action === 'outerForward') {
          const outer = outerRef.current;
          if (outer) review.go(action === 'outerBack' ? outer.back.ply : outer.forward.ply);
          else review[action === 'outerBack' ? 'toStart' : 'toEnd']();
          return;
        }
        review[action]();
      },
      [review],
    );

    React.useEffect(() => {
      if (!review) return;
      const onKey = (event: KeyboardEvent) => {
        const mods = { meta: event.metaKey, ctrl: event.ctrlKey, alt: event.altKey, defaultPrevented: event.defaultPrevented };
        const action = keyAction(event.key, mods, isTyping(event.target as HTMLElement | null), review.state(), rangedRef.current);
        if (!action) return;
        perform(action);
        event.preventDefault();
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [review, perform]);

    const onAction = perform;

    return (
      <StepControls view={{ ...view, ready: view.ready && !!review }} onAction={onAction}>
        {/* Beneath the steps: whatever other plugins put at the foot, the material strip first. */}
        <app.Extension name="review.stepControlsFoot" />
      </StepControls>
    );
  };
  app.registerExtension('review.stepControls', Panel);
}
